import React, { MouseEventHandler, useEffect, useState } from 'react'
import { Grid, Button, IconButton, InputAdornment, Typography, TextField, Box } from '@mui/material'
import clsx from 'clsx'
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material'

import i18n from 'i18n/index'
import AccountApi from 'apis/accountApi'
import history from 'utils/browserHistory'

import DEFAULT_RULES from './Rules'
import useStyles from './style'
import RecoverSuccess from '../RecoverSuccess'
import RulesRecoverPassword from './RulesRecoverPassword'

type Props = {
  token: string
  setToken: (newToken: string) => void
  handleLoading: (loading: boolean) => void
}

const RecoverPassword = (props: Props) => {
  const { token, setToken, handleLoading } = props
  const classes = useStyles()
  const [error, setError] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [rulesCheck] = useState(DEFAULT_RULES)
  const [validate, setValidate] = useState(false)

  const validatetoken = async () => {
    try {
      const responseValidate = await AccountApi.validateTokenForgotPassword(token)
      if (responseValidate && responseValidate.metadata.statuscode === 200) {
        handleLoading(false)
        return
      }
      handleLoading(false)
      setToken('')
      history.push('/')
    } catch (err) {
      handleLoading(false)
      setToken('')
      history.push('/')
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }
  const handleClickShowPasswordRepeat = () => {
    setShowPasswordRepeat(!showPasswordRepeat)
  }

  const handleMouseDownPassword = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault()
  }

  const updatePassword = async () => {
    if (newPassword !== repeatPassword) {
      setError(i18n.t('errorSecondPassword'))
      return
    }
    try {
      const responseUpdatePassword = await AccountApi.updatePassword(btoa(newPassword), token)
      if (responseUpdatePassword && responseUpdatePassword.metadata.statuscode === 200) {
        setValidate(true)
        return
      }
      setError(i18n.t('errorUpdatePassword'))
    } catch (err) {
      setError(i18n.t('errorUpdatePassword'))
      handleLoading(false)
    }
  }

  useEffect(() => {
    validatetoken()
    return () => {
      setToken('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (validate)
    return (
      <RecoverSuccess
        finishRecoverPassword={() => {
          setToken('')
          history.push('/')
        }}
        message={i18n.t('updatePasswordSuccess')}
      />
    )

  return (
    <Box id="container-recover-password">
      <Typography variant="h5" color="secondary.contrastText">
        {i18n.t('resetPassword')}
      </Typography>
      <TextField
        fullWidth
        id="input-new-password"
        color="primary"
        variant="standard"
        type={showPassword ? 'text' : 'password'}
        label={i18n.t('enterNewPassword')}
        size="small"
        value={newPassword}
        sx={{ marginTop: '10px' }}
        onChange={(e) => {
          const { value } = e.target
          setError('')
          setNewPassword(value)
          setRepeatPassword('')
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                sx={{ bgcolor: 'transparent' }}
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword as any as MouseEventHandler<HTMLButtonElement>}
                edge="end"
              >
                {showPassword ? (
                  <VisibilityOutlined sx={{ color: 'secondary.contrastText' }} />
                ) : (
                  <VisibilityOffOutlined sx={{ color: 'secondary.contrastText' }} />
                )}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <TextField
        id="input-repeat-password"
        color="primary"
        fullWidth
        variant="standard"
        className={clsx(classes.margin)}
        type={showPasswordRepeat ? 'text' : 'password'}
        label={i18n.t('repeatPassword')}
        size="small"
        disabled={Boolean(!rulesCheck.every((rule) => rule.validate(newPassword)))}
        value={repeatPassword}
        sx={{ marginTop: '10px' }}
        onChange={(e) => {
          const { value } = e.target
          setError('')
          setRepeatPassword(value)
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                sx={{ bgcolor: 'transparent' }}
                aria-label="toggle password visibility"
                onClick={handleClickShowPasswordRepeat}
                onMouseDown={handleMouseDownPassword as any as MouseEventHandler<HTMLButtonElement>}
                edge="end"
              >
                {showPasswordRepeat ? (
                  <VisibilityOutlined sx={{ color: 'secondary.contrastText' }} />
                ) : (
                  <VisibilityOffOutlined sx={{ color: 'secondary.contrastText' }} />
                )}
              </IconButton>
            </InputAdornment>
          )
        }}
        error={Boolean(error)}
      />
      <Typography id="text-forgot-password-error" color="error" variant="subtitle1">
        {error}
      </Typography>
      <RulesRecoverPassword title={`${i18n.t('mustContain')}:`} rulesCheck={rulesCheck} password={newPassword} />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Button
          sx={{ marginTop: '30px' }}
          color="primary"
          size="large"
          variant="contained"
          onClick={updatePassword}
          disabled={Boolean(!newPassword || !repeatPassword || !rulesCheck.every((rule) => rule.validate(newPassword)))}
        >
          {i18n.t('btnSave')}
        </Button>
      </Grid>
    </Box>
  )
}

export default RecoverPassword
